<script setup>
import HrTabPanels from './HrTabPanels.vue';

/**
 * Container that shows one HrPanel at a time, chosen by v-model rather than by a tab bar.
 *
 * The shape behind a multi-pane dialog, a drill-down or a wizard: the panes are steps the app moves
 * between, and nothing on screen offers them as a set to choose from. HrTabPanels is the same
 * mechanism answering to a tab bar, and says so to a screen reader; these panels announce as
 * ordinary content.
 *
 * v-model flows through attrs.
 *
 * @status recommended
 * @group navigation
 * @useWhen Switching between panes programmatically, with no tab bar offering them to the user
 * @avoidWhen The user picks the pane from a visible bar — use HrTabs with HrTabPanels
 * @related HrTabPanels
 */
defineOptions({ name: 'HrPanels' });

defineProps({
  /** Keeps inactive panels mounted, preserving their state. */
  keepAlive: {
    type: Boolean,
    default: false,
  },
  /**
   * Set when the panels sit inside a container that already scrolls, such as a dialog's content
   * area. The panel then sizes to what it is showing instead of owning its own scrolling, so the
   * container is the only scroller and short panels are not scrolled inside a taller box.
   */
  contentSized: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <hr-tab-panels v-bind="$props">
    <!-- @slot HrPanel components. -->
    <slot />
  </hr-tab-panels>
</template>
